/* istanbul ignore file */

import type { ComponentProps } from 'react';
import React, { forwardRef, memo, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, type TextInputInstance } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import { TextField } from '@sg/garnish';

// ────────────────────────────────────────────────────────────────────────────────

export const LocationSearchField = memo(
  forwardRef<TextInputInstance, LocationSearchFieldProps>((props, ref) => {
    const {
      value,
      onSubmit,
      onChangeText,
      onClear,
      isDisabled = false,
      placeholder,
      enterKeyHint,
      dataDetectorTypes,
      style,
    } = props;

    const a11ySelector = useA11ySelector('locations.search-field');
    const { formatMessage } = useIntl();

    /**
     * A fallback for when the `onSubmit` prop is not provided to re-focus
     * the input on the web platform.
     */
    const onSubmitFallback = useCallback(() => {
      if (typeof ref === 'function' || Platform.OS !== 'web') return;

      ref?.current?.focus();
    }, [ref]);

    return (
      <TextField
        ref={ref}
        hideNoticeSpace
        containerTestID="locationsearch.location-search-bar"
        submitButtonTestID="locationsearch.submit-button"
        testID="location-search-input"
        accessibilityLabel={formatMessage(messages.locationSearchPlaceholder)}
        placeholder={placeholder}
        style={style}
        clearButtonType="text"
        value={value}
        dataDetectorTypes={dataDetectorTypes}
        enterKeyHint={enterKeyHint}
        onChangeText={onChangeText}
        onClear={onClear}
        onSubmit={onSubmit ?? onSubmitFallback}
        hideSubmitButton={!onSubmit}
        disabled={isDisabled}
        clearBtnLabel={formatMessage(messages.locationSearchClear)}
        clearBtnAccessibilityHint={formatMessage(messages.locationSearchClearA11yHint)}
        submitBtnAccessibilityLabel={formatMessage(messages.locationSearchSubmit)}
        submitAccessibilityHint={formatMessage(messages.locationSearchSubmitA11yHint)}
        {...a11ySelector.props}
      />
    );
  }),
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationSearchPlaceholder: {
    defaultMessage: 'Search store, city, state, or zip',
    description: 'Location | Search Placeholder',
  },
  locationSearchClear: {
    defaultMessage: 'Clear',
    description: 'Location Search | Clear',
  },
  locationSearchClearA11yHint: {
    defaultMessage: 'Clears entered text',
    description: 'Location Search | Clear A11y Hint',
  },
  locationSearchSubmit: {
    defaultMessage: 'Submit',
    description: 'Location Search | Submit',
  },
  locationSearchSubmitA11yHint: {
    defaultMessage: 'Submits entered text',
    description: 'Location Search | Submit A11y Hint',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchFieldProps = Readonly<{
  value: string;
  isDisabled?: boolean;
  onClear?: () => void;
  placeholder: string;
}> &
  Pick<
    ComponentProps<typeof TextField>,
    'onSubmit' | 'onChangeText' | 'style' | 'enterKeyHint' | 'dataDetectorTypes'
  >;
